import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "src/common/state/hooks";
import { useAppDispatch } from "src/common/state/hooks";
import type { RagAgent } from "src/features/rag/api/ragApi";
import { fetchAgent } from "src/features/rag/api/ragApi";
import RagChat from "src/features/rag/components/RagChat";
import RagConversationLoader from "src/features/rag/components/RagConversationLoader";
import {
  fetchConversationsThunk,
  openConversationThunk,
  startNewConversation,
  setActiveConversationId,
  getActiveRagConversation,
  getActiveRagConversationId,
} from "src/features/rag/state/ragSlice";

// Wixarpedia is permanently bound to the shared "Sophie" RAG agent (id 3 —
// World famous Sophie from Wixar). There is intentionally no agent picker:
// the agent cannot be changed or unselected from this surface.
const SOPHIE_AGENT_ID = 3;

// Cadence and ceiling for the pending-answer poll below. Same shape as the
// document-processing poll in useOrgDocuments: tick while something is in
// flight, stop as soon as it lands. The ceiling (~2 min, comfortably past the
// slowest Sophie answer) is what keeps a thread whose answer never arrives —
// a backend error, say — from polling for the rest of the session.
const PENDING_ANSWER_POLL_INTERVAL = 3000;
const PENDING_ANSWER_POLL_MAX_ATTEMPTS = 40;

const WixarpediaChat: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [agent, setAgent] = useState<RagAgent | null>(null);
  const [loading, setLoading] = useState(true);
  const activeConversation = useAppSelector(getActiveRagConversation);
  const activeConversationId = useAppSelector(getActiveRagConversationId);

  // Bumped by "Nouvelle requête". Together with the active thread id it forms the
  // chat pane's React key — see chatKey below.
  const [newChatCounter, setNewChatCounter] = useState(0);

  const loadAgent = useCallback(async () => {
    try {
      const data = await fetchAgent(SOPHIE_AGENT_ID);
      setAgent(data);
    } catch (err) {
      console.error("Failed to load Wixarpedia agent:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAgent();
  }, [loadAgent]);

  useEffect(() => {
    void dispatch(fetchConversationsThunk());
  }, [dispatch]);

  // Re-read the open thread on arrival. Leaving mid-generation does NOT cancel
  // the answer: the backend keeps consuming the stream and files the assistant
  // turn even with nobody listening, so the copy Redux is holding is a turn or
  // two behind by the time the user comes back. Reading the id from a ref keeps
  // this to the mount it belongs to — later id changes come from the sidebar,
  // which fetches the thread itself.
  const threadOnMountRef = useRef(activeConversationId);
  useEffect(() => {
    if (threadOnMountRef.current) {
      void dispatch(openConversationThunk(threadOnMountRef.current));
    }
  }, [dispatch]);

  // Come back BEFORE the answer is filed and the thread ends on the question.
  // Poll until the assistant turn shows up, then stop; each refetch replaces
  // `activeConversation`, which re-arms this effect and re-hydrates the pane.
  const pollBudgetRef = useRef({ conversationId: 0, turnCount: 0, attemptsLeft: 0 });
  useEffect(() => {
    const messages = activeConversation?.messages;
    if (!activeConversation || !messages?.length) return undefined;
    if (messages[messages.length - 1].role !== "user") return undefined;

    // Budget is per pending question: a thread that goes quiet once (backend
    // error) stops being polled, but its next question gets a fresh allowance.
    const budget = pollBudgetRef.current;
    if (budget.conversationId !== activeConversation.id || budget.turnCount !== messages.length) {
      pollBudgetRef.current = {
        conversationId: activeConversation.id,
        turnCount: messages.length,
        attemptsLeft: PENDING_ANSWER_POLL_MAX_ATTEMPTS,
      };
    }
    if (pollBudgetRef.current.attemptsLeft <= 0) return undefined;

    const timer = setTimeout(() => {
      pollBudgetRef.current.attemptsLeft -= 1;
      void dispatch(openConversationThunk(activeConversation.id));
    }, PENDING_ANSWER_POLL_INTERVAL);

    return () => clearTimeout(timer);
  }, [activeConversation, dispatch]);

  const handleConversationSelect = (conversationId: number) => {
    void dispatch(openConversationThunk(conversationId));
  };

  const handleNewConversation = () => {
    dispatch(startNewConversation());
    setNewChatCounter((n) => n + 1);
  };

  // The first question of an unsaved chat opens a thread server-side; adopt its
  // id (so follow-ups append to it) and refresh the sidebar so it appears.
  // Deliberately does NOT set `activeConversation`, which would remount the pane
  // via chatKey and wipe the answer still streaming into it.
  const handleConversationStarted = (conversationId: number) => {
    dispatch(setActiveConversationId(conversationId));
    void dispatch(fetchConversationsThunk());
  };

  // Remounting is how a thread switch resets the pane: messages, input draft,
  // voice mode and dictation all reinitialise together, so nothing leaks from
  // the previous thread and history never reaches the text-to-speech path.
  const chatKey = activeConversation
    ? `conversation-${activeConversation.id}`
    : `new-${newChatCounter}`;

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-3">
          <div className="w-12 h-12 bg-gray-200 rounded-xl" />
          <div className="h-4 bg-gray-200 rounded w-32" />
        </div>
      </div>
    );
  }

  // Sophie is a curated shared agent; document_count drives the same
  // empty/disabled state RagChat uses on the Agents page.
  const hasDocuments = (agent?.document_count ?? 0) > 0;

  // Mirror the ScenarIA shell (ScenarioHelperChat) so the chat column lands
  // at the exact same width. The left column holds the conversation history;
  // the right one is an empty, borderless spacer (Wixarpedia has no agent
  // picker — the agent is fixed).
  return (
    <div
      className="grid h-full w-full overflow-hidden"
      style={{ gridTemplateColumns: "240px 1fr 280px" }}
    >
      <div className="bg-gray-100 px-3 pt-1 border-r border-gray-200 overflow-y-auto overflow-x-hidden">
        <RagConversationLoader
          onConversationSelect={handleConversationSelect}
          onNewConversation={handleNewConversation}
        />
      </div>

      <div className="min-w-0 overflow-hidden p-2">
        <div className="h-full bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col min-h-0">
          <RagChat
            key={chatKey}
            agentId={SOPHIE_AGENT_ID}
            hasDocuments={hasDocuments}
            showHeader={false}
            emptyText={t("pages.agents.chat.wixarpediaEmptyChat")}
            initialConversation={activeConversation}
            onConversationStarted={handleConversationStarted}
          />
        </div>
      </div>

      <div aria-hidden="true" />
    </div>
  );
};

export default WixarpediaChat;
